import MiniSearch from "minisearch"

const INDEX_URL = "/search.json"
const MAX_RESULTS = 8
const MIN_QUERY_LENGTH = 2

// Characters of context to show on either side of the matched term in a snippet.
const SNIPPET_LEAD = 60
const SNIPPET_TRAIL = 120

// Splits on whitespace/punctuation, then additionally emits camelCase parts
// while keeping the original token. Without this, code identifiers are opaque:
// "setTimeout" stays a single term, so a search for "timeout" misses it (prefix
// matching only reaches the front of a token). Docs here are full of such
// identifiers — readyToRender, prince_options, setTimeout.
const tokenize = (string) =>
  string
    .split(/[\s\p{P}\p{S}]+/u)
    .filter(Boolean)
    .flatMap((token) => {
      const parts = token.split(/(?<=[a-z0-9])(?=[A-Z])/).filter((part) => part.length > 1)
      return parts.length > 1 ? [token, ...parts] : [token]
    })

const SEARCH_OPTIONS = {
  // A heading match beats body copy, and a page-title match beats a heading:
  // someone typing "watermark" wants the Watermarks page above its own
  // sections. `pagetitle` is populated only for page-level entries (see
  // loadIndex), so this boost floats a page over its subsections *only* when
  // the query hits the page title — it can't hijack a query a section answers
  // better (e.g. "timeout" still ranks the setTimeout sections, not the page
  // that merely mentions the word).
  boost: { pagetitle: 8, section: 4, page: 2 },
  prefix: true,
  fuzzy: 0.2,
  combineWith: "AND",
}

const ESCAPES = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }
const escapeHtml = (string) => string.replace(/[&<>"']/g, (char) => ESCAPES[char])
const escapeRegExp = (string) => string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")

const queryTerms = (query) =>
  query
    .trim()
    .split(/\s+/)
    .filter((term) => term.length >= MIN_QUERY_LENGTH)

// Wraps every literal term occurrence in <mark>, on already-escaped text.
//
// Uses literal matching rather than the engine's match positions — a
// fuzzy/prefix hit points at terms that don't appear verbatim, so highlighting
// them reads like a ransom note. A prefix query ("accessibl") still marks the
// matching span of the word it completes ("Accessibl"e); a purely fuzzy hit
// marks nothing, which is the honest outcome.
function markTerms(escapedText, terms) {
  if (terms.length === 0) return escapedText
  const pattern = new RegExp(`(${terms.map(escapeRegExp).join("|")})`, "gi")
  return escapedText.replace(pattern, "<mark>$1</mark>")
}

// Builds the result snippet: a window of body text centred on the first literal
// term hit (or the head of the section when only a fuzzy match exists), with
// every term marked.
function snippet(text, terms) {
  const haystack = text.toLowerCase()

  let hit = -1
  for (const term of terms) {
    const found = haystack.indexOf(term.toLowerCase())
    if (found !== -1 && (hit === -1 || found < hit)) hit = found
  }

  const anchor = hit === -1 ? 0 : hit
  const start = Math.max(0, anchor - SNIPPET_LEAD)
  const end = Math.min(text.length, anchor + SNIPPET_TRAIL)

  let window = text.slice(start, end)
  if (start > 0) window = `…${window}`
  if (end < text.length) window = `${window}…`

  return markTerms(escapeHtml(window), terms)
}

export default function docSearch() {
  return {
    query: "",
    results: [],
    active: 0,
    open: false,
    // idle | loading | ready | error
    status: "idle",
    mini: null,
    entries: [],

    init() {
      this.$watch("query", () => this.search())

      // ⌘K / Ctrl-K from anywhere on the page.
      window.addEventListener("keydown", (event) => {
        if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
          event.preventDefault()
          this.$refs.input.focus()
          this.$refs.input.select()
        }
      })
    },

    // Fetched on first focus rather than page load: the index is ~14 KB gzipped
    // and most visitors never search.
    async loadIndex() {
      if (this.status === "loading" || this.status === "ready") return

      this.status = "loading"
      try {
        const response = await fetch(INDEX_URL)
        if (!response.ok) throw new Error(`${response.status} ${response.statusText}`)

        this.entries = await response.json()
        this.mini = new MiniSearch({
          idField: "uid",
          fields: ["pagetitle", "section", "page", "text"],
          tokenize,
          searchOptions: SEARCH_OPTIONS,
        })
        // Indexing 78 sections takes a few ms; not worth shipping a prebuilt
        // serialized index and the size that comes with it.
        //
        // pagetitle exists only on page-level entries (those with no section
        // heading); it carries the page title into a dedicated top-boost field
        // so a page ranks above its own subsections. Sections leave it null,
        // which MiniSearch skips.
        this.mini.addAll(
          this.entries.map((entry, uid) => ({
            ...entry,
            uid,
            pagetitle: entry.section == null ? entry.page : null,
          }))
        )

        this.status = "ready"
        this.search()
      } catch (error) {
        console.error("Could not load the search index:", error)
        this.status = "error"
      }
    },

    search() {
      const terms = queryTerms(this.query)
      if (this.status !== "ready" || terms.length === 0) {
        this.results = []
        this.open = terms.length > 0
        return
      }

      const query = this.query.trim()
      // Every term present is the better ranking, but a chatty question
      // ("how long are my pdfs kept") has terms that appear nowhere. Falling
      // back to OR still surfaces the pages worth reading.
      let hits = this.mini.search(query)
      if (hits.length === 0) hits = this.mini.search(query, { combineWith: "OR" })

      this.results = hits.slice(0, MAX_RESULTS).map(({ id }) => {
        const entry = this.entries[id]
        return {
          titleHtml: markTerms(escapeHtml(entry.title), terms),
          page: entry.page,
          section: entry.section,
          url: entry.url,
          group: entry.group,
          snippet: snippet(entry.text, terms),
        }
      })

      this.active = 0
      this.open = true
    },

    move(delta) {
      if (this.results.length === 0) return
      const count = this.results.length
      this.active = (this.active + delta + count) % count
      this.$refs.results
        ?.querySelectorAll("[data-result]")
        [this.active]?.scrollIntoView({ block: "nearest" })
    },

    go() {
      const result = this.results[this.active]
      if (result) window.location.href = result.url
    },

    close() {
      this.open = false
    },

    reset() {
      this.query = ""
      this.results = []
      this.close()
    },

    get noResults() {
      return (
        this.open &&
        this.status === "ready" &&
        this.results.length === 0 &&
        queryTerms(this.query).length > 0
      )
    },
  }
}
